import React, { useEffect, useMemo, useState } from "react";
import { FaTimes, FaCopy, FaCheck, FaLink, FaMagic, FaInfoCircle, FaExclamationTriangle } from "react-icons/fa";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { handleTokenAndPostRequest } from "../utils/utils";

function CopyMessageButton({ text, label = "Copy", disabled }) {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    if (!text || disabled) return;
    try {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) { /* ignore */ }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      disabled={disabled || !text}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "8px 14px", borderRadius: 8,
        border: "none",
        background: copied ? "#16a34a" : "#0d9488",
        color: "#fff", fontSize: 13, fontWeight: 600,
        cursor: disabled || !text ? "not-allowed" : "pointer",
        opacity: disabled || !text ? 0.5 : 1,
        transition: "background 0.15s",
      }}
    >
      {copied ? <FaCheck /> : <FaCopy />}
      {copied ? "Copied" : label}
    </button>
  );
}

function agentSignature({ agent_name, agency_name_short }) {
  const a = (agent_name || "").trim();
  const ag = (agency_name_short || "").trim();
  if (a && ag) return `${a}\n${ag}`;
  return a || ag || "";
}

// Strip en/em dashes so the generated copy reads in the agent's own voice when
// they paste it. We replace with a comma or sentence break depending on
// surrounding whitespace.
function stripDashes(text) {
  if (!text) return text;
  return text
    .replace(/\s+[—–]\s+/g, ", ")
    .replace(/[—–]/g, ",");
}

// SMS — short, casual, no marketing voice, no "STOP to opt out".
function buildSmsMessages(data) {
  const first = (data.contact_first_name || "").trim();
  const greeting = first ? `Hey ${first}` : "Hey";
  const a = (data.agent_name || "").trim();
  const ag = (data.agency_name_short || "").trim();
  const signLine = a ? `\n\n${a}${ag ? `, ${ag}` : ""}` : "";

  // Value check — capture the address tied to this contact's mobile. No
  // name/email ask in the message; the link lands on /psl/ which just needs
  // an address.
  const valueCheck =
    `${greeting}, ${a || "it's me"} here. Great meeting you. ` +
    `Have a play with this if you're curious what your place is worth, ` +
    `just pop the address in: ${data.search_link}${signLine}`;

  // Property tracker signup — fuller ask, name/email/phone collected on
  // signup. Slightly warmer tone since this is asking more of them.
  const tracker =
    `${greeting}, ${a || "it's me"} here. Happy to set you up on my property tracker. ` +
    `I'll flick you a quick value update + recent sales nearby each month, nothing spammy. ` +
    `Sign up here: ${data.notif_link}${signLine}`;

  return [
    {
      key: "value_check",
      title: "Value check",
      blurb: "Just captures their address tied to this contact's mobile. No name or email required.",
      body: valueCheck,
    },
    {
      key: "tracker_signup",
      title: "Property tracker signup",
      blurb: "Asks for name, email and phone. Use when you want their full details on file.",
      body: tracker,
    },
  ];
}

// Email — multi-line, subject + body. Sign-off block. Still casual but with
// the structure people expect from a real email.
function buildEmailMessages(data) {
  const first = (data.contact_first_name || "").trim();
  const greeting = first ? `Hi ${first},` : "Hi,";
  const sign = agentSignature(data);

  const valueCheckSubject = "Curious what your place is worth?";
  const valueCheckBody =
`${greeting}

Great meeting you. Thought you might find this handy. If you ever wonder what your place would go for in today's market, have a play with this:

${data.search_link}

Just pop the address in and it'll give you an instant estimate. No sign up needed.

Any questions, just hit reply.

Cheers,
${sign}`;

  const trackerSubject = "Quick value update on your place each month";
  const trackerBody =
`${greeting}

Good to meet you. I keep an eye on what's selling around your area and I can flick you a quick monthly update. Current value on your place, plus any recent sales nearby. Nothing spammy, just useful to have in the back pocket.

Takes about 30 seconds to set up:
${data.notif_link}

Let me know if you'd rather I just send it through once and you can decide from there.

Cheers,
${sign}`;

  return [
    {
      key: "value_check",
      title: "Value check",
      blurb: "Captures their address tied to this contact. No name/email/phone ask in the message.",
      subject: valueCheckSubject,
      body: valueCheckBody,
    },
    {
      key: "tracker_signup",
      title: "Property tracker signup",
      blurb: "Use when you want to collect their name, email and phone on signup.",
      subject: trackerSubject,
      body: trackerBody,
    },
  ];
}

function ChannelToggle({ value, onChange }) {
  const opt = (key, label) => (
    <button
      key={key}
      type="button"
      onClick={() => onChange(key)}
      style={{
        flex: 1,
        padding: "6px 12px",
        borderRadius: 999,
        border: "none",
        background: value === key ? "#0d9488" : "transparent",
        color: value === key ? "#fff" : "#6b7280",
        fontSize: 12, fontWeight: 600,
        cursor: "pointer",
        transition: "background 0.12s",
      }}
    >
      {label}
    </button>
  );
  return (
    <div style={{
      display: "inline-flex",
      borderRadius: 999,
      border: "1px solid #e5e7eb",
      background: "#f9fafb",
      padding: 2, gap: 2,
    }}>
      {opt("sms", "SMS")}
      {opt("email", "Email")}
    </div>
  );
}

function MessageSkeleton() {
  return (
    <div style={{
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      padding: 12,
      background: "#f9fafb",
    }}>
      <Skeleton width={140} height={14} />
      <div style={{ marginTop: 4 }}>
        <Skeleton width="90%" height={10} />
      </div>
      <div style={{
        marginTop: 10,
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: "10px 12px",
      }}>
        <Skeleton count={3} height={12} />
      </div>
      <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
        <Skeleton width={130} height={32} borderRadius={8} />
      </div>
    </div>
  );
}

export default function ContactShareLinksModal({ contact, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [channel, setChannel] = useState("sms");
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);

  // AI-rewritten variants per channel, keyed by channel ('sms' | 'email').
  // When set, these override the default buildSmsMessages/buildEmailMessages
  // output. Cleared when the user opens the popover and submits a new regen.
  const [aiMessages, setAiMessages] = useState({ sms: null, email: null });
  const [regenerating, setRegenerating] = useState(false);
  const [regenError, setRegenError] = useState("");
  const [showRegenPopover, setShowRegenPopover] = useState(false);
  const [regenExtra, setRegenExtra] = useState("");

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setData(null);
    const body = {};
    if (contact?.email) body.contact_email = contact.email;
    if (contact?.clean_mobile || contact?.mobile) {
      body.contact_mobile = contact.clean_mobile || contact.mobile;
    }
    handleTokenAndPostRequest(`${window.api_host}/agent-generate-contact-share-link`, body)
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data || res;
        if (!payload || !payload.short_code) {
          setError(payload?.message || "Could not generate share link.");
        } else {
          setData({
            ...payload,
            contact_first_name: payload.contact_first_name || contact?.first_name || "",
          });
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.message || "Could not generate share link.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [contact]);

  const defaultMessages = useMemo(() => {
    if (!data) return [];
    return channel === "email" ? buildEmailMessages(data) : buildSmsMessages(data);
  }, [data, channel]);

  // Merge AI-rewritten bodies/subjects on top of the default template metadata
  // (title + blurb stay from the local templates so the "what is this for"
  // copy is consistent).
  const messages = useMemo(() => {
    const ai = aiMessages[channel];
    if (!ai || !ai.length) return defaultMessages;
    return defaultMessages.map((m) => {
      const override = ai.find((x) => x.key === m.key);
      if (!override) return m;
      return {
        ...m,
        subject: override.subject ? stripDashes(override.subject) : m.subject,
        body: override.body ? stripDashes(override.body) : m.body,
      };
    });
  }, [defaultMessages, aiMessages, channel]);

  const handleRegenerate = async () => {
    if (!data || regenerating) return;
    setRegenerating(true);
    setRegenError("");
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/agent-regenerate-contact-share-messages`,
        {
          channel,
          contact_first_name: data.contact_first_name || "",
          agent_name: data.agent_name || "",
          agency_name_short: data.agency_name_short || "",
          search_link: data.search_link,
          notif_link: data.notif_link,
          extra_instructions: regenExtra.trim() || undefined,
        },
        { "Content-Type": "application/json" }
      );
      const payload = res?.data || res;
      const next = Array.isArray(payload?.messages) ? payload.messages : null;
      if (!next || !next.length) {
        setRegenError(payload?.message || "Could not regenerate messages.");
      } else {
        setAiMessages((prev) => ({ ...prev, [channel]: next }));
        setRegenExtra("");
      }
    } catch (e) {
      setRegenError(e?.response?.data?.message || "Could not regenerate messages.");
    } finally {
      setRegenerating(false);
    }
  };

  const stop = (e) => e.stopPropagation();

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1300,
        background: "rgba(15, 23, 42, 0.5)",
        display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center",
        padding: isMobile ? 0 : 16,
      }}
    >
      <div
        onClick={stop}
        style={{
          background: "#fff",
          borderRadius: isMobile ? "16px 16px 0 0" : 14,
          width: "100%", maxWidth: 580,
          maxHeight: isMobile ? "92vh" : "88vh",
          overflowY: "auto",
          boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "14px 18px",
          borderBottom: "1px solid #e5e7eb",
          position: "sticky", top: 0, background: "#fff", zIndex: 1,
        }}>
          <FaLink style={{ color: "#0d9488" }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
              Share a personalised link
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {[contact?.first_name, contact?.last_name].filter(Boolean).join(" ") || contact?.email || contact?.clean_mobile || contact?.mobile || "Contact"}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              border: "none", background: "transparent", cursor: "pointer",
              color: "#6b7280", padding: 6, borderRadius: 6,
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: isMobile ? "14px 14px 20px" : "16px 18px 22px" }}>
          {/* Explainer — what this modal is and a concrete "when do I use it"
              so the agent isn't guessing on first contact with the feature. */}
          <div style={{
            display: "flex", gap: 10, alignItems: "flex-start",
            background: "#f0fdfa",
            border: "1px solid #99f6e4",
            borderRadius: 10,
            padding: "10px 12px",
            marginBottom: 12,
            fontSize: 12, color: "#0f766e", lineHeight: 1.5,
          }}>
            <FaInfoCircle style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              These are drafts for you to copy and send from your own phone or email. The link inside each message ties the click back to this contact.
              <div style={{ marginTop: 4, color: "#0d9488" }}>
                Example: you meet someone at an open home, save them as a contact, then send the value check from your phone so any property they search shows up against their name.
              </div>
            </div>
          </div>

          {/* Heads-up: clicking the link yourself logs you in as the
              contact, and any action you take on the page can trigger real
              emails or notifications to them. */}
          <div style={{
            display: "flex", gap: 10, alignItems: "flex-start",
            background: "#fef3c7",
            border: "1px solid #fcd34d",
            borderRadius: 10,
            padding: "10px 12px",
            marginBottom: 12,
            fontSize: 12, color: "#92400e", lineHeight: 1.5,
          }}>
            <FaExclamationTriangle style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              Don't click the link yourself. It logs you in as the contact, so anything you do on the page can send them a real email or notification. Just share it with them.
            </div>
          </div>

          {/* Channel toggle + regenerate. Both stay visible (the regenerate
              button is disabled until the initial link has come back). */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 8, marginBottom: 12, flexWrap: "wrap",
          }}>
            <ChannelToggle value={channel} onChange={setChannel} />
            <div style={{ position: "relative" }}>
              <button
                type="button"
                onClick={() => {
                  if (regenerating || !data) return;
                  setShowRegenPopover((v) => !v);
                }}
                disabled={!data || regenerating}
                title="Rewrite both messages in your voice"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", borderRadius: 8,
                  border: `1px solid ${regenerating ? "#0f766e" : "#0d9488"}`,
                  background: regenerating ? "#0f766e" : "#fff",
                  color: regenerating ? "#fff" : "#0d9488",
                  fontWeight: 600, fontSize: 12,
                  cursor: (!data || regenerating) ? "not-allowed" : "pointer",
                  opacity: (!data || regenerating) ? 0.7 : 1,
                }}
              >
                {regenerating ? (
                  <>
                    <style>{`@keyframes csl-spin { to { transform: rotate(360deg); } }`}</style>
                    <span aria-hidden style={{
                      width: 10, height: 10, borderRadius: "50%",
                      border: "2px solid rgba(255,255,255,0.4)",
                      borderTopColor: "#fff",
                      display: "inline-block",
                      animation: "csl-spin 0.7s linear infinite",
                    }} />
                    Regenerating…
                  </>
                ) : (
                  <>
                    <FaMagic size={10} /> Regenerate
                  </>
                )}
              </button>
              {showRegenPopover && !regenerating && (
                <>
                  <div
                    onClick={() => setShowRegenPopover(false)}
                    style={{ position: "fixed", inset: 0, zIndex: 40, background: "transparent" }}
                  />
                  <div
                    style={{
                      position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 41,
                      width: 300, maxWidth: "calc(100vw - 32px)",
                      background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
                      boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
                      padding: 12,
                    }}
                    onKeyDown={(e) => { if (e.key === "Escape") setShowRegenPopover(false); }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                      Extra instructions (optional)
                    </div>
                    <textarea
                      value={regenExtra}
                      onChange={(e) => setRegenExtra(e.target.value)}
                      rows={3}
                      maxLength={500}
                      autoFocus
                      placeholder="e.g. met them at the Mosman open home, mention I'm doing appraisals this Saturday"
                      style={{
                        width: "100%", boxSizing: "border-box",
                        padding: "8px 10px", borderRadius: 8,
                        border: "1px solid #d1d5db", fontSize: 12,
                        fontFamily: "inherit", color: "#111827", background: "#fff",
                        resize: "vertical", outline: "none",
                      }}
                    />
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                      <button
                        onClick={() => setShowRegenPopover(false)}
                        style={{
                          padding: "6px 10px", borderRadius: 8,
                          border: "1px solid #d1d5db", background: "#fff",
                          color: "#374151", fontWeight: 600, fontSize: 12, cursor: "pointer",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          setShowRegenPopover(false);
                          handleRegenerate();
                        }}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          padding: "6px 10px", borderRadius: 8,
                          border: "1px solid #0d9488", background: "#0d9488",
                          color: "#fff", fontWeight: 600, fontSize: 12, cursor: "pointer",
                        }}
                      >
                        <FaMagic size={10} /> Regenerate
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {regenError && (
            <div style={{
              background: "#fef2f2", color: "#991b1b",
              border: "1px solid #fecaca", borderRadius: 8,
              padding: "8px 10px", fontSize: 12, marginBottom: 10,
            }}>
              {regenError}
            </div>
          )}

          {error && (
            <div style={{
              background: "#fef2f2", color: "#991b1b",
              border: "1px solid #fecaca", borderRadius: 8,
              padding: "10px 12px", fontSize: 13,
            }}>
              {error}
            </div>
          )}

          {(loading || (!error && data)) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {(loading || regenerating) && (
                <>
                  <MessageSkeleton />
                  <MessageSkeleton />
                </>
              )}
              {!loading && !regenerating && data && messages.map((m) => (
                <div
                  key={m.key}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    padding: 12,
                    background: "#f9fafb",
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 2 }}>
                    {m.title}
                  </div>
                  <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8 }}>
                    {m.blurb}
                  </div>

                  {channel === "email" && m.subject && (
                    <div style={{
                      background: "#fff",
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      padding: "8px 12px",
                      fontSize: 12,
                      color: "#374151",
                      marginBottom: 6,
                      display: "flex", alignItems: "center", gap: 8,
                    }}>
                      <span style={{ color: "#9ca3af", fontWeight: 600, flexShrink: 0 }}>Subject</span>
                      <span style={{ fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {m.subject}
                      </span>
                    </div>
                  )}

                  <div style={{
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: "10px 12px",
                    fontSize: 13,
                    color: "#111827",
                    lineHeight: channel === "email" ? 1.55 : 1.5,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    marginBottom: 10,
                    fontFamily: channel === "sms"
                      ? "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                      : undefined,
                  }}>
                    {m.body}
                  </div>

                  <div style={{
                    display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap",
                  }}>
                    {channel === "email" && m.subject && (
                      <CopyMessageButton
                        text={m.subject}
                        label="Copy subject"
                      />
                    )}
                    <CopyMessageButton
                      text={m.body}
                      label={channel === "email" ? "Copy body" : "Copy message"}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
